import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Divider,
  Button,
  useColorModeValue,
  Card,
  CardBody,
  Badge,
  Icon,
  SimpleGrid,
  Avatar,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ElementType, useEffect, useState } from "react";
import { Customer } from "../utils/types";
import { Mail, Phone, Calendar } from "lucide-react";
import { FaWhatsapp, FaFacebook, FaInstagram } from "react-icons/fa";
import { getCustomerById } from "../hooks/customers";

const ViewCustomerPage = () => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { orgId, customerId } = useParams<{ orgId: string; customerId: string }>();
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "gray.800");

  const formatDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString() : "N/A";

  useEffect(() => {
    const getCustomerData = async () => {
      if (customerId && orgId) {
        const response = await getCustomerById(orgId, customerId);
        if (response.success && response.data) {
          setCustomer(response.data);
        } else {
          setError("Failed to fetch customer details");
        }
        setLoading(false);
      }
    };
    getCustomerData();
  }, [orgId, customerId]);

  if (loading)
    return (
      <VStack h="100vh" justify="center">
        <Spinner color="green.600" size="xl" />
        <Text color="gray.500">Loading customer details...</Text>
      </VStack>
    );

  if (!customer)
    return (
      <Box p={8}>
        <Text color="red.500">{error || "Customer not found"}</Text>
      </Box>
    );

  return (
    <Box bg="#f5f7fa" minH="100vh" p={{ base: 4, md: 8 }}>
      {/* Header Section */}
      <HStack justify="space-between" mb={8}>
        <Heading size="lg" color="gray.800">
          Customer Profile
        </Heading>
        <Button
          colorScheme="green"
          bg="#006400"
          color="white"
          onClick={() =>
            navigate(`/organization/${orgId}/customers/${customer.id}/edit`)
          }
        >
          Edit Customer
        </Button>
      </HStack>

      {/* Profile Header */}
      <Card bg={cardBg} shadow="md" rounded="xl" mb={6}>
        <CardBody>
          <HStack spacing={6} align="center">
            <Avatar
              name={`${customer.first_name} ${customer.second_name}`}
              size="xl"
              bg="green.500"
              color="white"
            />
            <VStack align="start" spacing={1}>
              <Heading size="md">
                {`${customer.title ?? ""} ${customer.first_name ?? ""} ${customer.second_name ?? ""}`.trim() ||
                  "Unnamed Customer"}
              </Heading>
              <Text color="gray.600">
                {customer.display_name || customer.email || "No display name"}
              </Text>
              <HStack spacing={2}>
                <Badge colorScheme="green" variant="subtle">
                  Active
                </Badge>
                {customer.source && (
                  <Badge colorScheme="blue" variant="outline">
                    {customer.source}
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Basic Info Section */}
      <Card bg={cardBg} shadow="sm" rounded="lg" mb={6}>
        <CardBody>
          <Heading size="sm" mb={4} color="gray.700">
            Contact Information
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <HStack>
              <Icon as={Mail} color="gray.500" />
              <Text>{customer.email || "N/A"}</Text>
            </HStack>
            <HStack>
              <Icon as={Phone} color="gray.500" />
              <Text>{customer.phone || "N/A"}</Text>
            </HStack>
            <HStack>
              <Icon as={Calendar} color="gray.500" />
              <Text>Created: {formatDate(customer.created_at)}</Text>
            </HStack>
            <HStack>
              <Icon as={Calendar} color="gray.500" />
              <Text>Updated: {formatDate(customer.updated_at)}</Text>
            </HStack>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Location Section */}
      <Card bg={cardBg} shadow="sm" rounded="lg" mb={6}>
        <CardBody>
          <Heading size="sm" mb={3} color="gray.700">
            Location
          </Heading>
          <VStack align="start" spacing={1} color="gray.600" fontSize="sm">
            <Text><strong>Address:</strong> {customer.address || "N/A"}</Text>
            <Text><strong>City:</strong> {customer.city || "N/A"}</Text>
            <Text><strong>State:</strong> {customer.state || "N/A"}</Text>
            <Text><strong>Country:</strong> {customer.country || "N/A"}</Text>
            <Text><strong>Postal Code:</strong> {customer.postal_code || "N/A"}</Text>
          </VStack>
        </CardBody>
      </Card>

      {/* CRM & Notes Section */}
      <Card bg={cardBg} shadow="sm" rounded="lg" mb={6}>
        <CardBody>
          <Heading size="sm" mb={3} color="gray.700">
            CRM Details
          </Heading>
          <VStack align="start" spacing={2} fontSize="sm" color="gray.700">
            <Text>
              <strong>Source:</strong> {customer.source || "N/A"}
            </Text>
            <Divider />
            <Text fontWeight="semibold">Notes</Text>
            {Array.isArray(customer?.lead?.lead_notes) &&
            customer?.lead?.lead_notes.length > 0 ? (
              <VStack align="stretch" spacing={3}>
                {customer.lead.lead_notes.map((note) => (
                  <Box
                    key={note.id}
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    borderWidth="1px"
                  >
                    <Text>{note.content}</Text>
                    <HStack justify="space-between" mt={1}>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(note.created_at).toLocaleString()}
                      </Text>
                      {note.written_by && (
                        <Badge colorScheme="blue" fontSize="0.65rem">
                          By {note.created_by}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="gray.500">No notes added yet</Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Social Section */}
      <Card bg={cardBg} shadow="sm" rounded="lg">
        <CardBody>
          <Heading size="sm" mb={3} color="gray.700">
            Social Profiles
          </Heading>
          <HStack spacing={3} wrap="wrap">
            {customer.whatsapp ? (
              <Button
                as="a"
                href={`https://wa.me/${customer.whatsapp}`}
                target="_blank"
                size="sm"
                leftIcon={<Icon as={FaWhatsapp as ElementType} />}
                colorScheme="green"
              >
                WhatsApp
              </Button>
            ) : (
              <Badge colorScheme="gray">WhatsApp: N/A</Badge>
            )}

            {customer.facebook_id ? (
              <Button
                as="a"
                href={`https://facebook.com/${customer.facebook_id}`}
                target="_blank"
                size="sm"
                colorScheme="facebook"
                leftIcon={<Icon as={FaFacebook as ElementType} />}
              >
                Facebook
              </Button>
            ) : (
              <Badge colorScheme="gray">Facebook: N/A</Badge>
            )}

            {customer.instagram_id ? (
              <Button
                as="a"
                href={`https://instagram.com/${customer.instagram_id}`}
                target="_blank"
                size="sm"
                colorScheme="pink"
                leftIcon={<Icon as={FaInstagram as ElementType} />}
              >
                Instagram
              </Button>
            ) : (
              <Badge colorScheme="gray">Instagram: N/A</Badge>
            )}
          </HStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default ViewCustomerPage;
